import React, { useState } from "react";
import {
  Barcode,
  Binary,
  Building2,
  ClipboardCheck,
  CloudCog,
  Coins,
  FileSignature,
  IdCard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Settings2,
  Stamp,
  Tag,
  User,
  Users,
  X,
} from "lucide-react";
import { ISalesRepInfo } from "../../models/epcCustomer.model";
import toast from "react-hot-toast";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import Button, { ButtonType } from "../Assets/Button";

interface AddRepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (repData: ISalesRepInfo) => Promise<void>;
  isLoading: boolean | undefined;
}

const AddSalesRepModal: React.FC<AddRepModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [formData, setFormData] = useState<ISalesRepInfo>({
    name: "",
    mobileNumber: "",
    email: "",
    address: "",
    gst: "",
    panNumber: "",
    bankAccountNumber: "",
    bankAccountName: "",
    ifsc: "",
    companyName: "",
    category: "Commission Dealer",
    fileCharge: undefined,
    portalCharge: undefined,
    commissionValue: undefined,
    commissionType: undefined,
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev: ISalesRepInfo) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "category") {
      if (value === "Commission Dealer") {
        setFormData((prev) => ({
          ...prev,
          fileCharge: undefined,
          portalCharge: undefined,
        }));
      }

      if (value === "Channel Partner") {
        setFormData((prev) => ({
          ...prev,
          commissionValue: undefined,
          commissionType: undefined,
        }));
      }
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!formData.mobileNumber.trim()) {
      toast.error("Mobile number is required");
      return false;
    }

    if (!/^\d{10}$/.test(formData.mobileNumber)) {
      toast.error("Mobile number must be 10 digits");
      return false;
    }

    if (!formData.address.trim()) {
      toast.error("Address is required");
      return false;
    }

    if (!formData.panNumber) {
      toast.error("PAN Number is required");
      return false;
    }

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber.trim())) {
      toast.error("Invalid PAN format (e.g., ABCDE1234F)");
      return false;
    }

    if (
      formData.gst &&
      !/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/.test(
        formData.gst.trim(),
      )
    ) {
      toast.error("Invalid GST format (e.g., 22AAAAA0000A1Z5)");
      return false;
    }

    if (formData.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc.trim())) {
      toast.error("Invalid IFSC format (e.g., ABCD0123456)");
      return false;
    }

    if (formData.category === "Commission Dealer") {
      if (
        formData.commissionValue === undefined ||
        formData.commissionValue <= 0
      ) {
        toast.error("Commission value is required for Commission Dealer");
        return false;
      }

      if (!formData.commissionType) {
        toast.error("Commission type is required for Commission Dealer");
        return false;
      }
    }

    if (formData.category === "Channel Partner") {
      if (
        formData.fileCharge === undefined ||
        formData.fileCharge === null ||
        formData.fileCharge <= 0
      ) {
        toast.error("File charge is required for Channel Partner");
        return false;
      }

      if (
        formData.portalCharge === undefined ||
        formData.portalCharge === null ||
        formData.portalCharge <= 0
      ) {
        toast.error("Portal charge is required for Channel Partner");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      setFormData({
        name: "",
        mobileNumber: "",
        address: "",
        gst: "",
        panNumber: "",
        bankAccountNumber: "",
        bankAccountName: "",
        ifsc: "",
        companyName: "",
        category: "Commission Dealer",
      });
      onClose();
    } catch (error) {
      console.error("Error adding sales rep:", error);
    }
  };

  const handleWheelPrevent = usePreventWheelChange();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-xl border border-gray-100">
          {/* Header */}
          <div className="px-8 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center">
                  <Users className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Add Sales Representative
                  </h2>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-8 max-h-[60vh] overflow-y-auto">
            <div className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-6 text-xs">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-600" />
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 
                         border-gray-200 hover:border-gray-300"
                      placeholder="Enter full name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-600" />
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono border-gray-200 hover:border-gray-300"
                      placeholder="Enter mobile number"
                      maxLength={10}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData?.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 resize-none border-gray-200 hover:border-gray-300"
                    placeholder="Enter complete address"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-600" />
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 border-gray-200 hover:border-gray-300"
                  >
                    <option value="Commission Dealer">Commission Dealer</option>
                    <option value="Channel Partner">Channel Partner</option>
                  </select>
                </div>

                {formData.category === "Commission Dealer" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Coins className="w-4 h-4 text-gray-600" />
                        Commission Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="commissionValue"
                        value={formData.commissionValue ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            commissionValue: Number(e.target.value),
                          }))
                        }
                        onWheel={handleWheelPrevent}
                        className="w-full px-4 py-3 border rounded-md focus:ring-2 outline-none border-gray-200 focus:ring-blue-500/20"
                        placeholder="Enter commission value"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-gray-600" />
                        Commission Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="commissionType"
                        value={formData.commissionType ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            commissionType: e.target.value as
                              | "per_kw"
                              | "per_order",
                          }))
                        }
                        className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none border-gray-200"
                      >
                        <option value="">Select commission type</option>
                        <option value="per_kw">Per kW</option>
                        <option value="per_order">Per Order</option>
                      </select>
                    </div>
                  </div>
                )}

                {formData.category === "Channel Partner" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4 text-gray-600" />
                        File Charge <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="fileCharge"
                        value={formData.fileCharge ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            fileCharge: Number(e.target.value),
                          }))
                        }
                        onWheel={handleWheelPrevent}
                        className="w-full px-4 py-3 border rounded-md focus:ring-2 outline-none border-gray-200 focus:ring-blue-500/20"
                        placeholder="Enter file charge"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <CloudCog className="w-4 h-4 text-gray-600" />
                        Portal Charge <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="portalCharge"
                        value={formData.portalCharge ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            portalCharge: Number(e.target.value),
                          }))
                        }
                        onWheel={handleWheelPrevent}
                        className="w-full px-4 py-3 border rounded-md focus:ring-2 outline-none border-gray-200 focus:ring-blue-500/20"
                        placeholder="Enter portal charge"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-600" />
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 border-gray-200 hover:border-gray-300"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              {/* Business Information */}
              <div className="space-y-6 text-xs">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-6 bg-green-500 rounded-full"></div>
                  Business Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <IdCard className="w-4 h-4 text-gray-600" />
                      PAN Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono border-gray-200 hover:border-gray-300"
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      style={{ textTransform: "uppercase" }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-600" />
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Stamp className="w-4 h-4 text-gray-600" />
                    GST Number
                  </label>
                  <input
                    type="text"
                    name="gst"
                    value={formData.gst}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono border-gray-200 hover:border-gray-300"
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
              </div>

              {/* Banking Information */}
              <div className="space-y-6 text-xs">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
                  Banking Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FileSignature className="w-4 h-4 text-gray-600" />
                      Bank Account Name
                    </label>
                    <input
                      type="text"
                      name="bankAccountName"
                      value={formData.bankAccountName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                      placeholder="Account holder name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Barcode className="w-4 h-4 text-gray-600" />
                      Bank Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono hover:border-gray-300"
                      placeholder="Enter account number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Binary className="w-4 h-4 text-gray-600" />
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono border-gray-200 hover:border-gray-300"
                    placeholder="ABCD0123456"
                    maxLength={11}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-50 px-8 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button
                type={ButtonType.Cancel}
                onClick={onClose}
                disabled={isLoading}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.Submit}
                onClick={handleSubmit}
                disabled={isLoading}
                className="text-xs inline-flex items-center gap-1"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? "Adding..." : "Add Sales Representative"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSalesRepModal;
